import React from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import {
    DA_Title,
    DA_TitleTag,
} from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { useBackgroundColor } from "@danishagro/cms/src/blocks/hooks/useBackgroundColor/useBackgroundColor.hook";
import { usePadding } from "src/blocks/hooks/usePadding/usePadding.hook";
import { SpotsProps } from "./Spots.interface";
import { useIconColor } from "./hooks/useIconColor/useIconColor.hook";
import Spot from "./components/Spot.component";
import { useAlignment } from "./hooks/useAlignment.hook";
import { useStackOnMobile } from "./hooks/useStackOnMobile/useStackOnMobile.hook";
import S from "./Spots.module.scss";

const Spots = (props: SpotsProps) => {
    // Settings
    const { alignmentClass } = useAlignment(props.headerAlignment, S);
    const { backgroundColorClass, hasBackgroundColor, alternativeButtonTheme } = useBackgroundColor(
        props.backgroundColor
    );

    const { iconColorClassName } = useIconColor(props.iconColor);
    const { paddingClass } = usePadding(props.isCompact, hasBackgroundColor);
    const { stackOnMobileClassName } = useStackOnMobile(props.itemStackOnMobile);

    // Check if IGNORE_HEIGHT is set in the settings
    const ignoreHeight = props.settings?.includes("IGNORE_HEIGHT");

    // Calculate grid columns based on spotsPrLine
    const getGridColumns = () => {
        if (props.spotsPrLine && props.spotsPrLine > 0 && Number.isInteger(props.spotsPrLine)) {
            return `repeat(${props.spotsPrLine}, 1fr)`;
        }
        return undefined; // Use default CSS grid behavior
    };

    const gridColumnsStyle = getGridColumns();

    return (
        <div
            className={cn(
                S.wrapper,
                backgroundColorClass,
                paddingClass,
                iconColorClassName,
                alignmentClass
            )}
        >
            <DA_Container noVerticalPadding>
                <div className={S.inner}>
                    {/** Content */}
                    <div className={S.header}>
                        {/** Tagline */}
                        {props.tagline ? <span className={S.tagline}>{props.tagline}</span> : null}

                        {/** Title */}
                        {props.headline ? (
                            <DA_Title h2 tag={DA_TitleTag.H2} noMargin fullWidth>
                                {props.headline}
                            </DA_Title>
                        ) : null}

                        {/** Text */}
                        {props.text ? <DA_Text html={props.text} noPadding /> : null}
                    </div>

                    {/** Spots */}
                    <div
                        className={cn(S.items, stackOnMobileClassName)}
                        style={
                            gridColumnsStyle
                                ? ({
                                      "--desktop-grid-columns": gridColumnsStyle,
                                  } as React.CSSProperties)
                                : undefined
                        }
                    >
                        {props.items?.map((item, index) => {
                            return (
                                <Spot
                                    {...item}
                                    alignment={props.itemAlignment}
                                    key={index}
                                    alternativeButtonTheme={alternativeButtonTheme}
                                    ignoreHeight={ignoreHeight}
                                />
                            );
                        })}
                    </div>
                </div>
            </DA_Container>
        </div>
    );
};

export default Spots;
